import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type StartDateContextType = {
  startDate: Date | null;
  setStartDate: (date: Date) => void;
  currentRamadanDay: number;
  isDateSelected: boolean;
  calculateRamadanDay: (date?: Date) => number;
};

const StartDateContext = createContext<StartDateContextType | undefined>(undefined);

export const StartDateProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [isDateSelected, setIsDateSelected] = useState(false);

  // Function to calculate the Ramadan day based on a given date (defaults to today)
  const calculateRamadanDay = React.useCallback((date?: Date) => {
    if (!startDate) return 1;
    
    const targetDate = date || new Date();
    // Normalize both dates to midnight to avoid time zone issues
    const normalizedTargetDate = new Date(targetDate);
    normalizedTargetDate.setHours(0, 0, 0, 0);
    
    const normalizedStartDate = new Date(startDate);
    normalizedStartDate.setHours(0, 0, 0, 0);
    
    const diffTime = normalizedTargetDate.getTime() - normalizedStartDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    // The startDate is day 1 of Ramadan
    // If today is the same as startDate, it's day 1
    // If today is 1 day after startDate, it's day 2, etc.
    return diffDays <= 0 ? 1 : diffDays + 1;
  }, [startDate]);

  // Calculate the current day of Ramadan based on today's date
  const currentRamadanDay = React.useMemo(() => {
    return calculateRamadanDay();
  }, [calculateRamadanDay]);

  // Load saved start date from localStorage
  useEffect(() => {
    const savedStartDate = localStorage.getItem('ramadanStartDate');
    if (savedStartDate) {
      setStartDate(new Date(savedStartDate));
      setIsDateSelected(true);
    }
  }, []);

  // Save the start date to localStorage whenever it changes
  useEffect(() => {
    if (startDate) {
      localStorage.setItem('ramadanStartDate', startDate.toISOString());
      setIsDateSelected(true);
    }
  }, [startDate]);

  return (
    <StartDateContext.Provider value={{ 
      startDate, 
      setStartDate, 
      currentRamadanDay, 
      isDateSelected,
      calculateRamadanDay 
    }}>
      {children}
    </StartDateContext.Provider>
  );
};

export const useStartDate = () => {
  const context = useContext(StartDateContext);
  
  if (context === undefined) {
    throw new Error('useStartDate must be used within a StartDateProvider');
  }
  
  return context;
}; 